import {
  AlertTriangle,
  Lightbulb,
  TrendingUp,
  Layers,
  ArrowUpRight,
  Zap,
  Phone,
  MessageSquare,
  Users,
  Sparkles,
  BookMarked,
  Timer,
  Target,
  Radio,
  ClipboardList,
  ChevronRight,
  Wand2,
  Rocket,
  ShieldCheck,
} from "lucide-react";
import { useDemo } from "@/demo/DemoContext";

/* --- Data --------------------------------------------------------- */

const SIGNALS = [
  {
    id: "signal-gap",
    kind: "ATTENTION",
    accent: "#F59E0B",
    Icon: AlertTriangle,
    headline: "17.3% conversion rate. Target is 21%.",
    rows: [
      { k: "Gap", v: "3.7 percentage points", tone: "#F59E0B" },
      { k: "Diagnosed cause", v: "Day 5–7 drop-off in 7-Day sequence — 23% of warm leads go cold after Day 5 with no voice touch" },
      { k: "Opportunity", v: "Add a Day 6 voice re-engagement to the 7-Day sequence", tone: "#00C2A8", strong: true },
    ],
  },
  {
    id: "signal-sms",
    kind: "OPPORTUNITY",
    accent: "#00C2A8",
    Icon: Lightbulb,
    headline: "SMS click-through rate is 41% — email is 11%.",
    rows: [
      { k: "Signal", v: "SMS is 3.7× more effective at driving booking-link clicks", tone: "#00C2A8" },
      { k: "Current usage", v: "1 SMS per sequence" },
      { k: "Opportunity", v: "Add SMS-first approach for Hot leads (score 80+) — send SMS before email on Day 1", tone: "#00C2A8", strong: true },
    ],
  },
  {
    id: "signal-tradeshow",
    kind: "TREND",
    accent: "#0EA5E9",
    Icon: TrendingUp,
    headline: "Trade show leads convert at 24% vs. 12% for web form leads.",
    rows: [
      { k: "Signal", v: "Trade show leads outperform 2:1", tone: "#0EA5E9" },
      { k: "Current approach", v: "Same sequence for all sources" },
      { k: "Opportunity", v: 'Build a dedicated Trade Show Fast-Track with a personal "great to meet you" voice opener', tone: "#00C2A8", strong: true },
    ],
  },
];

const ROADMAP = [
  {
    id: "rm-1",
    rank: 1,
    Icon: Phone,
    opportunity: "Add Day 6 voice re-touch to 7-Day sequence",
    source: "Insight 4 — nurturing gap",
    impact: "+4%",
    impactSub: "conversion",
    effort: "Low",
    effortDetail: "1 day",
    priorityTone: "#F59E0B",
  },
  {
    id: "rm-2",
    rank: 2,
    Icon: MessageSquare,
    opportunity: "SMS-first ordering for Hot leads",
    source: "Insight 4 — 98% SMS open rate",
    impact: "+2–3%",
    impactSub: "conversion",
    effort: "Low",
    effortDetail: "half day",
    priorityTone: "#00C2A8",
  },
  {
    id: "rm-3",
    rank: 3,
    Icon: Radio,
    opportunity: "Trade Show dedicated sequence with custom voice opener",
    source: "Insight 3 — trade show #1 lead source; 3-month nurture outperforms one-off",
    impact: "+3%",
    impactSub: "conversion",
    effort: "Medium",
    effortDetail: "2 days",
    priorityTone: "#0EA5E9",
  },
  {
    id: "rm-4",
    rank: 4,
    Icon: Wand2,
    opportunity: "Personalized email using prospect's recent publications",
    source: "Insight 3 — personalized outreach 5–8× response lift",
    impact: "+5–8%",
    impactSub: "response rate",
    effort: "Medium",
    effortDetail: "3 days",
    priorityTone: "#00C2A8",
  },
  {
    id: "rm-5",
    rank: 5,
    Icon: Zap,
    opportunity: "Post-show badge-scan auto-import + sequence trigger",
    source: "Failure Scenario — trade show leads go cold in 2–3 weeks",
    impact: "~30",
    impactSub: "leads / event",
    effort: "Medium",
    effortDetail: "2 days",
    priorityTone: "#0EA5E9",
  },
  {
    id: "rm-6",
    rank: 6,
    Icon: ShieldCheck,
    opportunity: "Account health-check re-engagement for converted SQLs",
    source: "Failure Scenario — accounts go quiet before renewal",
    impact: "Retention",
    impactSub: "uplift",
    effort: "High",
    effortDetail: "1 week",
    priorityTone: "#7A9BB5",
  },
];

const TIER2 = {
  id: "tier-current",
  label: "Current State",
  tag: "Tier 2 · Growth Engine",
  headline: "MQL nurture · Voice · Email · SMS active",
  metric: "17.3%",
  metricLabel: "conversion",
  accent: "#0EA5E9",
  items: [
    "Intake Scorer routing leads to Fast-Track / 7-Day / 14-Day",
    "Vapi voice agent handling outbound + inbound qualification",
    "Make.com email + SMS sequences across all 3 cadences",
    "Handoff Agent creating CRM tasks on every demo booking",
  ],
};

const TIER3 = {
  id: "tier-future",
  label: "Full Commercial Operations",
  tag: "Tier 3 · Enterprise Automation",
  headline: "Everything in Tier 2 + always-on customer ops",
  metric: "21%+",
  metricLabel: "projected conversion",
  accent: "#00C2A8",
  featured: true,
  items: [
    "24/7 customer support voice agent (post-sale)",
    "Real-time pipeline dashboard for exec team",
    "Quarterly optimization loop — agent tuning + A/B testing",
    "Account health-check re-engagement for renewals",
    "Multi-region compliance + escalation routing",
  ],
};

/* --- Sub-components ---------------------------------------------- */

function SignalCard({ s, i }) {
  const { Icon } = s;
  return (
    <article
      data-testid={s.id}
      className="card-glass card-interactive p-6 enter-up relative overflow-hidden"
      style={{
        animationDelay: `${i * 100}ms`,
        borderLeft: `3px solid ${s.accent}`,
        background: `linear-gradient(135deg, ${s.accent}0B 0%, transparent 100%), var(--bg-elevated)`,
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <div
          className="flex items-center justify-center rounded-md"
          style={{ width: 34, height: 34, background: `${s.accent}14`, border: `1px solid ${s.accent}40` }}
        >
          <Icon size={16} color={s.accent} />
        </div>
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9.5,
            letterSpacing: "0.16em",
            color: s.accent,
            textTransform: "uppercase",
            padding: "3px 8px",
            borderRadius: 4,
            background: `${s.accent}12`,
            border: `1px solid ${s.accent}30`,
          }}
        >
          {s.kind}
        </span>
      </div>

      <h3
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 700,
          fontSize: 17,
          letterSpacing: "-0.02em",
          color: "var(--text-primary)",
          lineHeight: 1.3,
          marginBottom: 18,
        }}
      >
        {s.headline}
      </h3>

      <ul className="space-y-3">
        {s.rows.map((r, ri) => (
          <li key={ri}>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9.5,
                letterSpacing: "0.12em",
                color: "var(--text-muted)",
                textTransform: "uppercase",
                marginBottom: 2,
              }}
            >
              {r.k}
            </div>
            <div
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                lineHeight: 1.55,
                color: r.tone || "var(--text-primary)",
                fontWeight: r.strong ? 600 : 400,
                letterSpacing: r.strong ? "-0.005em" : "0",
              }}
            >
              {r.v}
            </div>
          </li>
        ))}
      </ul>
    </article>
  );
}

function Signals() {
  return (
    <section data-testid="signals-section" className="space-y-4">
      <div className="flex items-center gap-3">
        <div
          className="flex items-center justify-center rounded-md"
          style={{ width: 26, height: 26, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.30)" }}
        >
          <Sparkles size={12} color="#00C2A8" />
        </div>
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            letterSpacing: "0.14em",
            color: "var(--text-muted)",
            textTransform: "uppercase",
          }}
        >
          This Month's Key Signals
        </div>
        <div style={{ flex: 1, height: 1, background: "var(--border-divider)" }} />
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            color: "var(--text-muted)",
            letterSpacing: "0.06em",
          }}
        >
          3 SIGNALS · JUNE 2026
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {SIGNALS.map((s, i) => <SignalCard key={s.id} s={s} i={i} />)}
      </div>
    </section>
  );
}

function EffortPill({ effort, detail }) {
  const palette = {
    Low: { c: "#22C55E", bg: "rgba(34, 197, 94, 0.10)", bd: "rgba(34, 197, 94, 0.30)" },
    Medium: { c: "#F59E0B", bg: "rgba(245, 158, 11, 0.10)", bd: "rgba(245, 158, 11, 0.30)" },
    High: { c: "#EF4444", bg: "rgba(239, 68, 68, 0.08)", bd: "rgba(239, 68, 68, 0.30)" },
  }[effort];
  return (
    <div className="flex items-center gap-2">
      <span
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10.5,
          padding: "3px 8px",
          borderRadius: 4,
          color: palette.c,
          background: palette.bg,
          border: `1px solid ${palette.bd}`,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        {effort}
      </span>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: "var(--text-muted)" }}>
        {detail}
      </span>
    </div>
  );
}

function RoadmapTable() {
  return (
    <article data-testid="roadmap-table-card" className="card-glass p-6">
      <header className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(14, 165, 233, 0.10)", border: "1px solid rgba(14, 165, 233, 0.25)" }}
          >
            <ClipboardList size={14} color="#0EA5E9" />
          </div>
          <div>
            <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              Automation Opportunity Roadmap
            </h2>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              Ranked by impact / effort · sourced from blueprint
            </div>
          </div>
        </div>
        <span
          className="pill pill-teal"
          style={{ fontSize: 10 }}
        >
          <span className="pill-dot pill-dot-pulse" />
          6 in queue
        </span>
      </header>

      <div style={{ overflowX: "auto" }}>
        <table
          data-testid="roadmap-table"
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 820 }}
        >
          <thead>
            <tr>
              {["Rank", "Opportunity", "Blueprint Source", "Est. Conversion Impact", "Build Effort"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.12em",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    borderBottom: "1px solid var(--border-divider)",
                    fontWeight: 500,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROADMAP.map((r, i) => {
              const { Icon } = r;
              return (
                <tr
                  key={r.id}
                  data-testid={r.id}
                  className="enter-up"
                  style={{
                    animationDelay: `${i * 50}ms`,
                    transition: "background 150ms ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(22, 35, 51, 0.5)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td
                    style={{
                      padding: "14px 12px",
                      borderBottom: "1px solid rgba(30, 48, 72, 0.4)",
                      verticalAlign: "top",
                      width: 70,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 999,
                          background: r.rank <= 2
                            ? "linear-gradient(135deg, #00C2A8, #0EA5E9)"
                            : "var(--bg-elevated)",
                          border: r.rank <= 2 ? "none" : "1px solid var(--border-divider)",
                          color: r.rank <= 2 ? "#0F1923" : "var(--text-secondary)",
                          fontFamily: "'JetBrains Mono', monospace",
                          fontWeight: 700,
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: r.rank <= 2 ? "0 0 12px rgba(0, 194, 168, 0.3)" : "none",
                        }}
                      >
                        {r.rank}
                      </span>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "14px 12px",
                      borderBottom: "1px solid rgba(30, 48, 72, 0.4)",
                      verticalAlign: "top",
                    }}
                  >
                    <div className="flex items-start gap-2.5">
                      <div
                        className="flex items-center justify-center rounded-md flex-shrink-0"
                        style={{
                          width: 28, height: 28,
                          background: `${r.priorityTone}12`,
                          border: `1px solid ${r.priorityTone}30`,
                          marginTop: 1,
                        }}
                      >
                        <Icon size={13} color={r.priorityTone} />
                      </div>
                      <div
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: "var(--text-primary)",
                          letterSpacing: "-0.005em",
                          lineHeight: 1.4,
                        }}
                      >
                        {r.opportunity}
                      </div>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "14px 12px",
                      borderBottom: "1px solid rgba(30, 48, 72, 0.4)",
                      verticalAlign: "top",
                    }}
                  >
                    <div className="flex items-start gap-1.5">
                      <BookMarked size={11} color="var(--text-muted)" style={{ marginTop: 3, flexShrink: 0 }} />
                      <span
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: 12,
                          color: "var(--text-secondary)",
                          lineHeight: 1.45,
                          fontStyle: "italic",
                        }}
                      >
                        {r.source}
                      </span>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "14px 12px",
                      borderBottom: "1px solid rgba(30, 48, 72, 0.4)",
                      verticalAlign: "top",
                    }}
                  >
                    <div className="flex items-baseline gap-2">
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontWeight: 700,
                          fontSize: 15,
                          color: "#00C2A8",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {r.impact}
                      </span>
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 10,
                          letterSpacing: "0.06em",
                          color: "var(--text-muted)",
                          textTransform: "uppercase",
                        }}
                      >
                        {r.impactSub}
                      </span>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "14px 12px",
                      borderBottom: "1px solid rgba(30, 48, 72, 0.4)",
                      verticalAlign: "top",
                    }}
                  >
                    <EffortPill effort={r.effort} detail={r.effortDetail} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer legend */}
      <div
        className="mt-4 pt-4 flex flex-wrap items-center gap-4"
        style={{ borderTop: "1px dashed var(--border-divider)" }}
      >
        <div className="flex items-center gap-2">
          <span
            style={{
              width: 20, height: 20, borderRadius: 999,
              background: "linear-gradient(135deg, #00C2A8, #0EA5E9)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              color: "#0F1923", fontWeight: 700,
            }}
          >
            1
          </span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: "var(--text-secondary)", letterSpacing: "0.02em" }}>
            Highlighted = quick wins (Low effort, high impact)
          </span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Timer size={12} color="var(--text-muted)" />
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: "var(--text-muted)", letterSpacing: "0.02em" }}>
            Total build time · ~5 weeks
          </span>
        </div>
      </div>
    </article>
  );
}

function TierPanel({ tier, i }) {
  return (
    <article
      data-testid={tier.id}
      className={`card-glass p-6 enter-up relative overflow-hidden ${tier.featured ? "card-interactive" : ""}`}
      style={{
        animationDelay: `${i * 100}ms`,
        borderLeft: `3px solid ${tier.accent}`,
        background: tier.featured
          ? `linear-gradient(135deg, ${tier.accent}0F 0%, transparent 100%), var(--bg-elevated)`
          : "var(--bg-elevated)",
        boxShadow: tier.featured ? `0 0 24px ${tier.accent}18` : undefined,
      }}
    >
      {tier.featured && (
        <div
          style={{
            position: "absolute",
            top: -20,
            right: -20,
            color: `${tier.accent}0E`,
          }}
        >
          <Rocket size={140} strokeWidth={1} />
        </div>
      )}

      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.14em",
              color: tier.accent,
              textTransform: "uppercase",
              padding: "3px 8px",
              borderRadius: 4,
              background: `${tier.accent}14`,
              border: `1px solid ${tier.accent}30`,
            }}
          >
            {tier.tag}
          </span>
          {tier.featured && (
            <span
              className="pill pill-teal"
              style={{ fontSize: 10 }}
            >
              <span className="pill-dot pill-dot-pulse" />
              Recommended
            </span>
          )}
        </div>

        <h3
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 800,
            fontSize: 22,
            letterSpacing: "-0.02em",
            color: "var(--text-primary)",
            marginBottom: 4,
          }}
        >
          {tier.label}
        </h3>
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            color: "var(--text-secondary)",
            marginBottom: 20,
          }}
        >
          {tier.headline}
        </div>

        {/* Metric */}
        <div
          className="rounded-lg p-4 mb-5 flex items-baseline gap-3"
          style={{
            background: `${tier.accent}0A`,
            border: `1px dashed ${tier.accent}40`,
          }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700,
              fontSize: 34,
              color: tier.accent,
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            {tier.metric}
          </div>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              letterSpacing: "0.10em",
              color: "var(--text-muted)",
              textTransform: "uppercase",
            }}
          >
            {tier.metricLabel}
          </div>
        </div>

        <ul className="space-y-2.5">
          {tier.items.map((it, ii) => (
            <li key={ii} className="flex items-start gap-2.5">
              <ChevronRight size={13} color={tier.accent} style={{ marginTop: 3, flexShrink: 0 }} />
              <span
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 12.5,
                  color: "var(--text-primary)",
                  lineHeight: 1.5,
                }}
              >
                {it}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function FullAutomation() {
  const { openBooking } = useDemo();
  return (
    <section data-testid="full-automation" className="space-y-5">
      <div className="flex items-center gap-3">
        <div
          className="flex items-center justify-center rounded-md"
          style={{ width: 26, height: 26, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.30)" }}
        >
          <Layers size={12} color="#00C2A8" />
        </div>
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            letterSpacing: "0.14em",
            color: "var(--text-muted)",
            textTransform: "uppercase",
          }}
        >
          What Full Automation Looks Like
        </div>
        <div style={{ flex: 1, height: 1, background: "var(--border-divider)" }} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative">
        <TierPanel tier={TIER2} i={0} />
        <TierPanel tier={TIER3} i={1} />

        {/* Center arrow between panels (desktop only) */}
        <div
          className="hidden lg:flex"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 5,
            pointerEvents: "none",
          }}
        >
          <div
            className="rounded-full flex items-center justify-center"
            style={{
              width: 44, height: 44,
              background: "var(--bg-deep)",
              border: "1px solid rgba(0, 194, 168, 0.40)",
              boxShadow: "0 0 20px rgba(0, 194, 168, 0.30)",
              color: "#00C2A8",
            }}
          >
            <ArrowUpRight size={18} strokeWidth={2.5} />
          </div>
        </div>
      </div>

      {/* CTA */}
      <div
        className="card-glass p-5 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between enter-up"
        style={{
          background:
            "linear-gradient(135deg, rgba(0, 194, 168, 0.08) 0%, rgba(14, 165, 233, 0.05) 100%), var(--bg-elevated)",
          border: "1px solid rgba(0, 194, 168, 0.30)",
        }}
      >
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="flex items-center justify-center rounded-lg flex-shrink-0"
            style={{
              width: 42, height: 42,
              background: "linear-gradient(135deg, #00C2A8, #0EA5E9)",
              boxShadow: "0 0 20px rgba(0, 194, 168, 0.35)",
            }}
          >
            <Target size={20} color="#0F1923" strokeWidth={2.5} />
          </div>
          <div>
            <div
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: 16,
                letterSpacing: "-0.01em",
                color: "var(--text-primary)",
                lineHeight: 1.3,
              }}
            >
              Book Your Growth Audit
            </div>
            <div
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                color: "var(--text-secondary)",
                marginTop: 4,
                lineHeight: 1.5,
                maxWidth: 640,
              }}
            >
              45-minute working session with our team to map the exact path from your current 17.3% conversion to a Tier 3, 21%+ operation — using the roadmap above as the blueprint.
            </div>
          </div>
        </div>

        <button
          data-testid="growth-audit-cta"
          onClick={openBooking}
          className="flex items-center gap-2 rounded-lg flex-shrink-0"
          style={{
            padding: "12px 20px",
            background: "linear-gradient(135deg, #00C2A8, #0EA5E9)",
            color: "#0F1923",
            fontFamily: "Inter, sans-serif",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "-0.005em",
            border: "none",
            cursor: "pointer",
            transition: "transform 150ms ease, box-shadow 150ms ease",
            boxShadow: "0 0 20px rgba(0, 194, 168, 0.35)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 28px rgba(0, 194, 168, 0.50)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 0 20px rgba(0, 194, 168, 0.35)";
          }}
        >
          Book Your Growth Audit
          <ArrowUpRight size={16} />
        </button>
      </div>
    </section>
  );
}

/* --- Page --------------------------------------------------------- */

export default function GrowthIntel() {
  return (
    <div data-testid="growth-intel-page" className="max-w-[1400px] mx-auto space-y-8">
      {/* Header */}
      <section data-testid="growth-header" className="enter-up">
        <div className="flex items-start gap-4">
          <div
            style={{
              width: 4,
              alignSelf: "stretch",
              background: "linear-gradient(180deg, #F59E0B, #00C2A8, #0EA5E9)",
              borderRadius: 4,
              minHeight: 68,
            }}
          />
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.14em",
                color: "var(--accent-teal)",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Module · Learning & optimization
            </div>
            <h1
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                fontSize: 28,
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
                lineHeight: 1.2,
                marginBottom: 8,
              }}
            >
              Growth Intel
            </h1>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--text-secondary)",
                maxWidth: 860,
              }}
            >
              What the system has learned from{" "}
              <span style={{ color: "#0EA5E9", fontWeight: 600 }}>487 leads</span>,{" "}
              <span style={{ color: "#00C2A8", fontWeight: 600 }}>23 voice calls</span>, and{" "}
              <span style={{ color: "#F59E0B", fontWeight: 600 }}>1,247 emails</span> — and what to build next.
            </p>
          </div>
        </div>
      </section>

      {/* Signals */}
      <Signals />

      {/* Roadmap */}
      <RoadmapTable />

      {/* Tier compare + CTA */}
      <FullAutomation />
    </div>
  );
}
